import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "item",
    "planMonthly",
    "planYearly",
    "textMonthly",
    "textYearly",
    "btnFree",
    "btnPro",
    "priceAmount", "pricePeriod"
  ]

  connect() {
    this.selectedPlanId = null
    this.selectedPlanType = null

    const initialPeriod = this.currentBillingPeriod()
    this.carryOverSelection(initialPeriod)

    // The feature paywall fires this when the user clicks "Upgrade to <tier>".
    // Pre-select the matching plan card so the upgrade modal opens highlighted
    // on the right tier instead of the default (Pro).
    this._featurePaywallListener = (e) => {
      const tier = e.detail && e.detail.tier
      if (!tier) return
      this.selectedPlanType = tier
      this.carryOverSelection(this.currentBillingPeriod())
    }
    document.addEventListener("feature-paywall:select-tier", this._featurePaywallListener)
  }

  disconnect() {
    if (this._featurePaywallListener) {
      document.removeEventListener("feature-paywall:select-tier", this._featurePaywallListener)
    }
  }

  currentBillingPeriod() {
    if (this.hasPlanYearlyTarget && this.planYearlyTargets.some(el => !el.classList.contains('d-none'))) {
      return 'yearly'
    }
    return 'monthly'
  }

  // New pill UI: each side has data-action="click->plan#setBilling" and data-billing-period.
  setBilling(event) {
    const period = event.currentTarget.dataset.billingPeriod
    if (!period) return
    if (this.currentBillingPeriod() === period) return
    this.applyBillingPeriod(period)
  }

  // Legacy form-switch checkbox: change->plan#toggleFields.
  toggleFields(event) {
    const period = event.target.checked ? 'yearly' : 'monthly'
    this.applyBillingPeriod(period)
  }

  applyBillingPeriod(period) {
    const isYearly = period === 'yearly'

    this.planMonthlyTargets.forEach(el => el.classList.toggle('d-none', isYearly))
    this.planYearlyTargets.forEach(el => el.classList.toggle('d-none', !isYearly))

    if (this.hasTextMonthlyTarget) {
      this.textMonthlyTarget.classList.toggle('is-inactive', isYearly)
      this.textMonthlyTarget.classList.toggle('text-black-25', isYearly)
    }
    if (this.hasTextYearlyTarget) {
      this.textYearlyTarget.classList.toggle('is-inactive', !isYearly)
      this.textYearlyTarget.classList.toggle('text-black-25', !isYearly)
    }

    const checkbox = this.element.querySelector('input.toggle-btn')
    if (checkbox && checkbox.checked !== isYearly) checkbox.checked = isYearly

    this.updatePrice(isYearly)
    this.carryOverSelection(period)
  }

  // Re-select the same plan type the user had selected (or Pro by default)
  // on the newly visible billing-period side, so the plan id matches the current period.
  carryOverSelection(period) {
    if (!this.hasItemTarget) return
    const wantedType = this.selectedPlanType || 'pro'
    const candidates = this.itemTargets.filter(el => el.dataset.planBillingType === period)
    const pool = candidates.length ? candidates : this.itemTargets.filter(el => this.isItemVisible(el))

    const item =
      pool.find(el => el.dataset.planType === wantedType) ||
      pool.find(el => el.dataset.planType === 'pro') ||
      pool.find(el => el.dataset.planDefault === 'true') ||
      pool[0]

    if (item) this.selectPlan({ currentTarget: item })
  }

  isItemVisible(el) {
    let node = el
    while (node && node !== this.element) {
      if (node.classList && node.classList.contains('d-none')) return false
      node = node.parentElement
    }
    return true
  }

  selectPlan(event) {
    const selected = event.currentTarget
    if (!selected || !selected.dataset) return

    const planId = selected.dataset.planId
    const planType = selected.dataset.planType

    const input = document.querySelector('input[name="selected_plan_id"]')
    if (input) input.value = planId

    this.itemTargets.forEach(el => el.classList.remove("select-plan"))
    selected.classList.add("select-plan")

    if (this.hasBtnFreeTarget && this.hasBtnProTarget) {
      if (planType === "free") {
        this.btnFreeTarget.classList.remove("d-none")
        this.btnProTarget.classList.add("d-none")
      } else {
        this.btnProTarget.classList.remove("d-none")
        this.btnFreeTarget.classList.add("d-none")
        const link = this.btnProTarget.querySelector('a')
        if (link && link.href) {
          const url = new URL(link.href)
          url.searchParams.set('plan_id', planId)
          link.href = url.toString()
        }
      }
    }

    this.selectedPlanId = planId
    this.selectedPlanType = planType
  }

  updatePrice(isYearly) {
    if (this.hasPriceAmountTarget) {
      this.priceAmountTargets.forEach(el => {
        el.textContent = isYearly ? "$348" : "$49"
      })
    }

    if (this.hasPricePeriodTarget) {
      this.pricePeriodTargets.forEach(el => {
        el.textContent = isYearly ? "/yearly" : "/month"
      })
    }
  }
}
